import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Calendar } from "@/components/ui/calendar"
import { CalendarIcon } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import { FilterType } from "./types"
import { filterOptions } from "./TalentContext"

// Utility function to check if two filters are equal
function areFiltersEqual(a: FilterType, b: FilterType): boolean {
  // Compare primitives
  if (a.level !== b.level || a.status !== b.status) {
    return false
  }

  // Compare arrays - handle undefined/null cases explicitly
  const aSkills = a.skills || [];
  const bSkills = b.skills || [];
  const aLanguages = a.languages || [];
  const bLanguages = b.languages || [];

  // If lengths differ, arrays are not equal
  if (aSkills.length !== bSkills.length || aLanguages.length !== bLanguages.length) {
    return false;
  }

  // Check each skill and language
  if (!aSkills.every(skill => bSkills.includes(skill)) ||
      !aLanguages.every(lang => bLanguages.includes(lang))) {
    return false;
  }
  
  // Compare dateRange
  if ((!a.dateRange && b.dateRange) || (a.dateRange && !b.dateRange)) {
    return false;
  }
  
  if (a.dateRange && b.dateRange) {
    if (a.dateRange.start !== b.dateRange.start || a.dateRange.end !== b.dateRange.end) {
      return false;
    }
  }
  
  return true;
}

type TalentFiltersProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: FilterType
  onApplyFilters: (filters: FilterType) => void
  onResetFilters: () => void
}

// Extracted filter section components
type FilterSectionProps = {
  localFilters: FilterType
  setLocalFilters: React.Dispatch<React.SetStateAction<FilterType>>
  selectedDate?: Date
  setSelectedDate: (date: Date | undefined) => void
}

function LevelFilter({ localFilters, setLocalFilters }: FilterSectionProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Experience Level</Label>
      <Select 
        value={localFilters.level} 
        onValueChange={(value) => setLocalFilters(prev => ({...prev, level: value}))}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select level" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All levels</SelectItem>
          {filterOptions.levels.map(level => (
            <SelectItem key={level} value={level}>{level}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function StatusFilter({ localFilters, setLocalFilters }: FilterSectionProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Status</Label>
      <Select 
        value={localFilters.status} 
        onValueChange={(value) => setLocalFilters(prev => ({...prev, status: value}))}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {filterOptions.statuses.map(status => (
            <SelectItem key={status} value={status}>{status}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

type MultiSelectFilterProps = FilterSectionProps & {
  title: string
  options: string[]
  selectedValues: string[]
  onToggle: (value: string) => void
  onClear: () => void
}

function MultiSelectFilter({ 
  title, 
  options,
  selectedValues,
  onToggle,
  onClear
}: MultiSelectFilterProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{title}</Label>
      <div className="mt-2">
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedValues.map(value => (
              <Badge 
                key={value} 
                variant="secondary" 
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => onToggle(value)}
              >
                {value}
                <span className="ml-1 text-xs">×</span>
              </Badge>
            ))}
            <Badge 
              variant="outline" 
              className="cursor-pointer"
              onClick={onClear}
            >
              Clear all
            </Badge>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {options.map(option => (
            <div key={option} className="flex items-center space-x-2">
              <Checkbox 
                id={`${title.toLowerCase()}-${option}`} 
                checked={selectedValues.includes(option)}
                onCheckedChange={() => onToggle(option)}
              />
              <Label 
                htmlFor={`${title.toLowerCase()}-${option}`}
                className="text-sm cursor-pointer"
              >
                {option}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function DateFilter({ selectedDate, setSelectedDate }: FilterSectionProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Last Updated Date</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? (
              format(selectedDate, "PPP")
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {selectedDate && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="mt-1" 
          onClick={() => setSelectedDate(undefined)}
        >
          Clear date
        </Button>
      )}
    </div>
  )
}

export function TalentFilters({ 
  open, 
  onOpenChange, 
  filters, 
  onApplyFilters, 
  onResetFilters 
}: TalentFiltersProps) {
  // Local state for filter values
  const [localFilters, setLocalFilters] = useState<FilterType>({
    level: "all",
    status: "all",
    skills: [],
    languages: []
  })
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  
  const isInitialMount = useRef(true)
  const prevFiltersRef = useRef(filters)
  const timeoutRef = useRef<number | null>(null)
  
  // Apply filters with debounce
  const debouncedApplyFilters = useCallback((updatedFilters: FilterType) => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
    }
    
    timeoutRef.current = window.setTimeout(() => {
      if (!areFiltersEqual(updatedFilters, prevFiltersRef.current)) {
        onApplyFilters(updatedFilters)
        prevFiltersRef.current = updatedFilters
      }
    }, 300)
  }, [onApplyFilters])
  
  // Reset local filters when the sheet opens
  useEffect(() => {
    if (open) {
      setLocalFilters({
        level: filters.level || "all",
        status: filters.status || "all",
        skills: [...(filters.skills || [])],
        languages: [...(filters.languages || [])]
      })
      setSelectedDate(filters.dateRange?.start ? new Date(filters.dateRange.start) : undefined)
      prevFiltersRef.current = filters
    }
  }, [open, filters])

  // Auto-apply filters when they change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (open) {
      const updatedFilters: FilterType = {
        ...localFilters,
        dateRange: selectedDate 
          ? { 
              start: format(selectedDate, 'yyyy-MM-dd'), 
              end: format(selectedDate, 'yyyy-MM-dd') 
            } 
          : undefined
      }
      
      debouncedApplyFilters(updatedFilters)
    }
  }, [localFilters, selectedDate, open, debouncedApplyFilters])
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleResetFilters = () => {
    const emptyFilters: FilterType = {
      level: "all",
      status: "all",
      skills: [],
      languages: []
    }
    
    setLocalFilters(emptyFilters)
    setSelectedDate(undefined)
    onResetFilters()
  }

  // Filter action handlers
  const toggleSkill = (skill: string) => {
    setLocalFilters(prev => {
      const skills = [...(prev.skills || [])]
      return {
        ...prev,
        skills: skills.includes(skill)
          ? skills.filter(s => s !== skill)
          : [...skills, skill]
      }
    })
  }

  const toggleLanguage = (language: string) => {
    setLocalFilters(prev => {
      const languages = [...(prev.languages || [])]
      return {
        ...prev,
        languages: languages.includes(language)
          ? languages.filter(l => l !== language)
          : [...languages, language]
      }
    })
  }

  const clearSkills = () => setLocalFilters(prev => ({...prev, skills: []}))
  const clearLanguages = () => setLocalFilters(prev => ({...prev, languages: []}))

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle>Advanced Filters</SheetTitle>
          <SheetDescription>
            Narrow down the talent pool with specific criteria
          </SheetDescription>
        </SheetHeader>
        
        <div className="py-2 space-y-6">
          <LevelFilter 
            localFilters={localFilters} 
            setLocalFilters={setLocalFilters}
            setSelectedDate={setSelectedDate}
          />
          
          <StatusFilter 
            localFilters={localFilters} 
            setLocalFilters={setLocalFilters}
            setSelectedDate={setSelectedDate}
          />
          
          <Separator />
          
          <MultiSelectFilter
            title="Skills"
            options={filterOptions.skills}
            selectedValues={localFilters.skills || []}
            onToggle={toggleSkill}
            onClear={clearSkills}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            setSelectedDate={setSelectedDate}
          />
          
          <Separator />
          
          <MultiSelectFilter
            title="Languages"
            options={filterOptions.languages}
            selectedValues={localFilters.languages || []}
            onToggle={toggleLanguage}
            onClear={clearLanguages}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            setSelectedDate={setSelectedDate}
          />
          
          <Separator />
          
          <DateFilter
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </div>
        
        <SheetFooter className="pt-6 gap-2">
          <Button 
            variant="outline" 
            onClick={handleResetFilters}
            className="w-full"
          >
            Reset All Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}